import React, { useEffect, useState, useCallback } from 'react';
import api from '../api-instance';
import { useNavigate, Link } from 'react-router-dom';
import NavBar from './NavBar';
import { Toaster } from 'react-hot-toast';

// Helper function to format large numbers
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return String(num);
};

// Helper function to format percentage
const formatPercentage = (num) => {
  return num.toFixed(1) + '%';
};

const BannerList = () => {

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannerList, setBannerList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingExpirations, setUpcomingExpirations] = useState([]);
  const [kpiMetrics, setKpiMetrics] = useState({
    totalBanners: 0,
    activeCampaigns: 0,
    totalImpressions: '0',
    avgCTR: '0%',
    allUsers: 0
  });

  const navigate = useNavigate();

  const fetchBanners = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await api.get('/banners');
      const banners = response.data || [];
      setBanners(banners);
      setBannerList(banners);

      const activeCount = banners.filter(banner => banner.status === 'live').length;
      
    } catch (err) {
      console.error('Failed to fetch banners', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // Search Functionality
  const handleSearch = (e) => {
    const input = e.target.value.toLowerCase();
    setSearchTerm(input);

    const filteredList = banners.filter(banner => {
      const name = banner.name?.toLowerCase() || '';
      const type = banner.type?.toLowerCase() || '';
      const contentIds = Array.isArray(banner.target?.contentIds) 
        ? banner.target.contentIds.join(' ').toLowerCase() 
        : '';
      return name.includes(input) || type.includes(input) || contentIds.includes(input);
    });
    setBannerList(filteredList);
  };

  // Pagination logic
  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = bannerList.slice(indexOfFirstTask, indexOfLastTask);

  const totalPages = Math.ceil(bannerList.length / tasksPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm('Are you sure you want to delete this banner?');
    if (!isConfirmed) {
      return;
    }
  
    try {
      await api.delete(`/banners/${id}`);
      fetchBanners();
    } catch (err) {
      alert(err.response?.data?.error || 'An error occurred while deleting the banner. Please try again.');
    }
  };

  const handleClone = async (id) => {
    try {
      const response = await api.post(`/banners/${id}/clone`);
      if (response.data?.id) {
        navigate(`/add-banner/${response.data.id}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to clone banner. Please try again.');
    }
  };
  const getPaginationPages = (currentPage, totalPages) => {
    // Show every page when there are 10 or fewer pages
    if (totalPages <= 10) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
  
    const pages = [];
    const addPage = (page) => {
      if (!pages.includes(page)) {
        pages.push(page);
      }
    };
  
    // Always show the first page
    addPage(1);
  
    if (currentPage <= 4) {
      // Example: 1 2 3 4 5 ... 20
      for (let page = 2; page <= 5; page++) {
        addPage(page);
      }
  
      pages.push('dots-right');
      addPage(totalPages);
    } else if (currentPage >= totalPages - 3) {
      // Example: 1 ... 16 17 18 19 20
      pages.push('dots-left');
  
      for (let page = totalPages - 4; page <= totalPages; page++) {
        addPage(page);
      }
    } else {
      // Example: 1 ... 7 8 9 ... 20
      pages.push('dots-left');
  
      for (let page = currentPage - 1; page <= currentPage + 1; page++) {
        addPage(page);
      }
  
      pages.push('dots-right');
      addPage(totalPages);
    }
  
    return pages;
  };
  
  const paginationPages = getPaginationPages(currentPage, totalPages);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-slate-600 text-sm font-medium">Loading...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      
      <NavBar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* TABLE */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">All Banners</h3>
              <p className="mt-1 text-sm text-slate-500">
                {bannerList.length === 0 
                  ? 'No banners' 
                  : `${bannerList.length} ${bannerList.length === 1 ? 'banner' : 'banners'} total`
                }
              </p>
            </div>
          </div>

          <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1 w-full sm:max-w-md">
              <input
                type="search"
                placeholder="Search banners..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
              />
            </div>
            <Link
              to="/add-banner"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Banner
            </Link>
          </div>
        
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Banner Position</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Story ID</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Targeting</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Banner</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Preview</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">End Date</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Tags</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Story URL</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {currentTasks.length > 0 ? (
                  currentTasks.map(banner => {
                    const assets = Array.isArray(banner.assets) ? banner.assets : [];
                    const target = banner.target || {};
                    const targetingMode = target.targetingMode || 'story';
                    const contentIds = Array.isArray(target.contentIds) ? target.contentIds.join(', ') : '';
                    const storyUrls = Array.isArray(target.storyUrls) ? target.storyUrls : [];
                    const sections = Array.isArray(target.sections) ? target.sections : [];
                    const tags = Array.isArray(target.tags) ? target.tags.join(', ') : '';
                    const firstStoryUrl = storyUrls.length > 0 ? storyUrls[0] : '';
                    
                    return (
                      <tr key={banner.id} className="hover:bg-slate-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-slate-900">#{banner.id}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-slate-900">{banner.name || banner.type}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contentIds ? (
                            <span className="text-xs font-mono text-slate-600 max-w-[200px]" title={contentIds}>
                              {contentIds}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              targetingMode === 'story_url' ? 'bg-orange-100 text-orange-700' :
                              targetingMode === 'story' ? 'bg-blue-100 text-blue-700' :
                              targetingMode === 'section' ? 'bg-purple-100 text-purple-700' :
                              'bg-teal-100 text-teal-700'
                            }`}>
                              {targetingMode === 'story_url' ? 'Story URL' : targetingMode === 'story' ? 'Story ID' : targetingMode === 'section' ? 'Section' : 'Section+Tags'}
                            </span>
                            {sections.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {sections.slice(0, 2).map((s, idx) => (
                                  <span key={idx} className="text-xs text-slate-500">{s}</span>
                                ))}
                                {sections.length > 2 && <span className="text-xs text-slate-400">+{sections.length - 2}</span>}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1.5">
                            {assets.map((asset, index) => (
                              <a
                                key={index}
                                rel="noreferrer"
                                href={asset.url || asset.previewUrl}
                                target='_blank'
                                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 font-medium transition-colors duration-200 w-fit"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span className="whitespace-nowrap">Banner {index + 1}</span>
                              </a>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-2">
                            {assets.map((asset, index) => (
                              <div key={index} className="relative group inline-block">
                                <img
                                  src={asset.url || asset.previewUrl}
                                  alt={`Banner ${index + 1} preview`}
                                  className="hover-zoom max-w-[120px] max-h-24 border border-slate-200 hover:border-indigo-300 cursor-pointer bg-white"
                                  style={{ width: 'auto', height: 'auto' }}
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="64" viewBox="0 0 80 64"%3E%3Crect fill="%23f1f5f9" width="80" height="64"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {banner.startAt ? (
                            <span className="text-sm text-slate-700">{new Date(banner.startAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {banner.endAt ? (
                            <span className="text-sm text-slate-700">{new Date(banner.endAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            banner.status === 'live' ? 'bg-green-100 text-green-800' :
                            banner.status === 'draft' ? 'bg-slate-100 text-slate-800' :
                            banner.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {banner.status === 'live' && (
                              <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
                            )}
                            {banner.status || 'draft'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {tags ? (
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {tags.split(', ').slice(0, 2).map((tag, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                                  {tag}
                                </span>
                              ))}
                              {tags.split(', ').length > 2 && (
                                <span className="text-xs text-slate-500">+{tags.split(', ').length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {firstStoryUrl ? (
                            <a
                              rel="noreferrer"
                              href={firstStoryUrl}
                              target='_blank'
                              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-slate-50 text-slate-700 rounded-md hover:bg-slate-100 font-medium transition-colors duration-200"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              Open
                            </a>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/add-banner/${banner.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors duration-200"
                              title="Edit banner"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </Link>
                            <button
                              onClick={() => handleClone(banner.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                              title="Clone banner"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Clone
                            </button>
                            <button
                              onClick={() => handleDelete(banner.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors duration-200"
                              title="Delete banner"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="11" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <svg className="w-12 h-12 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-slate-900">No banners found</p>
                          <p className="text-sm text-slate-500 mt-1">
                            {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating a new banner'}
                          </p>
                        </div>
                        {!searchTerm && (
                          <Link
                            to="/add-banner"
                            className="inline-flex items-center gap-2 px-4 py-2 mt-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create Banner
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
  {/* Mobile pagination */}
  <div className="flex flex-1 justify-between sm:hidden">
    <button
      type="button"
      onClick={() => paginate(currentPage - 1)}
      disabled={currentPage === 1}
      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      Previous
    </button>

    <button
      type="button"
      onClick={() => paginate(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      Next
    </button>
  </div>

  {/* Desktop pagination */}
  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
    <div>
      <p className="text-sm text-gray-700">
        Showing{' '}
        <span className="font-medium">
          {indexOfFirstTask + 1}
        </span>{' '}
        to{' '}
        <span className="font-medium">
          {Math.min(indexOfLastTask, bannerList.length)}
        </span>{' '}
        of{' '}
        <span className="font-medium">
          {bannerList.length}
        </span>{' '}
        results
      </p>
    </div>

    <div>
      <nav
        aria-label="Pagination"
        className="isolate inline-flex -space-x-px rounded-md shadow-xs"
      >
        {/* Previous button */}
        <button
          type="button"
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 inset-ring inset-ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="sr-only">Previous</span>

          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
            className="size-5"
          >
            <path
              d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
              clipRule="evenodd"
              fillRule="evenodd"
            />
          </svg>
        </button>

        {/* Page numbers and dots */}
        {paginationPages.map((page) => {
          if (page === 'dots-left' || page === 'dots-right') {
            return (
              <span
                key={page}
                className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 inset-ring inset-ring-gray-300"
              >
                ...
              </span>
            );
          }

          const isCurrentPage = page === currentPage;

          return (
            <button
              type="button"
              key={page}
              onClick={() => paginate(page)}
              aria-current={isCurrentPage ? 'page' : undefined}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 focus:outline-offset-0 ${
                isCurrentPage
                  ? 'z-10 bg-indigo-600 text-white focus-visible:outline-2 focus-visible:outline-indigo-600'
                  : 'text-gray-900 inset-ring inset-ring-gray-300 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          );
        })}

        {/* Next button */}
        <button
          type="button"
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 inset-ring inset-ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="sr-only">Next</span>

          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
            className="size-5"
          >
            <path
              d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
              fillRule="evenodd"
            />
          </svg>
        </button>
      </nav>
    </div>
  </div>
</div>
        </div>

      </div>

      <Toaster />
    </div>
  );
};

export default BannerList;
